import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"
import { ProfileData } from "./user"

interface Permissions {
	creationDate: string | undefined
	modificationDate: string | undefined
	createdBy: string | undefined
	modifiedBy: string | undefined
	version: number
	id: number
	enabled: boolean
	editable: boolean
	permissionName: string
}

interface Role {
	createdBy: string | undefined
	creationDate: string | undefined
	id: number
	modificationDate: string | undefined
	modifiedBy: string | undefined
	role: string
	version: number
}

interface UserCorporates {
	createdBy: string | undefined
	creationDate: string | undefined
	id: number
	modificationDate: string | undefined
	modifiedBy: string | undefined
	version: number
	corporateSenderId: string | undefined
	read: boolean
	write: boolean
}

export interface AuthResponse {
	id: number
	avatar?: File | undefined
	phone?: string | undefined
	username?: string
	email?: string
	fullName: string
	permissions: Permissions[]
	idBank: string
	corporateSenderId: string
	message: string
	imageString: string | null
	bankLogo: string
	corporateLogo: string
	primaryColor: string | null
	secondaryColor: string | null
	corporatePrimaryColor: string
	corporateSecondaryColor: string
	language: string
	userPasswordReset: boolean
	userCorporates: UserCorporates[]
	report: boolean
	distinte: boolean
	resetToken: string | undefined
	roles: Role[]
}

export interface AuthState {
	user: AuthResponse | undefined
	loading: boolean
	resendLoading: boolean
	error: ErrorResponse | undefined
	isOtpRequired: boolean
	isAuthenticate: boolean
	resetToken?: string | undefined
	userPasswordReset?: boolean
}

interface AuthPayload {
	username: string
	password: string
}

export interface OtpPayload {
	otp: string
	user: string
}

interface OtpResendPayload {
	username: string
}

export interface VerifyOtpResponse {
	message?: string
	bank?: string
	rolesList?: string[]
	permissions: Permissions[]
}

interface ICreateResponse {}

interface IPatchPayload {
	idUser: string
	profileData: ProfileData
}

interface ErrorResponse {
	message: string
	[key: string]: any
}

const axiosInstance = axios.create()
axiosInstance.defaults.withCredentials = true
axiosInstance.defaults.baseURL = process.env.REACT_APP_USER_MANAGEMENT_API_URL

export const authenticateUser = createAsyncThunk<
	AuthResponse,
	AuthPayload,
	{ rejectValue: ErrorResponse }
>(
	"auth/authenticateUser",
	async ({ username, password }: AuthPayload, { rejectWithValue }) => {
		const config: AxiosRequestConfig = {
			headers: {
				"Content-Type": "application/json",
			},
		}

		try {
			const response: AxiosResponse<AuthResponse> = await axios.post(
				`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiauthenticate/v2/signin`,
				{ username, password: btoa(password) },
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<ErrorResponse>

			console.log("RESPONSE:", axiosError.response)
			const backendError = axiosError.response?.data
			return rejectWithValue(backendError ?? { message: "generic" })
		}
	}
)

export const refreshAccessToken = createAsyncThunk(
	"auth/refreshAccessToken",
	async (thunkAPI) => {
		const headerConfig = {
			headers: {
				"Content-Type": "application/json",
			},
			withCredentials: true,
		}
		try {
			const response: AxiosResponse = await axiosInstance.post(
				`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiauthenticate/v2/refresh-token`,
				undefined,
				headerConfig
			)

			return response.data
		} catch (error) {
			if (error.response.status === "403") thunkAPI.dispatch(logout())
			return thunkAPI.rejectWithValue(
				"Failed to refresh access token" + JSON.stringify(error)
			)
		}
	}
)

export const logout = createAsyncThunk("auth/logout", async (thunkAPI) => {
	const headerConfig = {
		headers: {
			"Content-Type": "application/json",
		},
		withCredentials: true,
	}
	try {
		const response: AxiosResponse = await axiosInstance.post(
			`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiauthenticate/v2/signout`,
			undefined,
			headerConfig
		)

		return response.data
	} catch (error) {
		const axiosError = error as AxiosError<string>

		return thunkAPI.rejectWithValue(axiosError?.response?.data ?? "generic")
	}
})

export const resendOtp = createAsyncThunk<
	boolean,
	OtpResendPayload,
	{ rejectValue: string }
>(
	"auth/resendOtp",
	async ({ username }: OtpResendPayload, { rejectWithValue }) => {
		const config: AxiosRequestConfig = {
			headers: {
				"Content-Type": "application/json",
			},
		}

		try {
			const response: AxiosResponse<boolean> = await axios.post(
				`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiauthenticate/resend/otp`,
				{ username },
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<string>
			return rejectWithValue(axiosError?.response?.data ?? "generic")
		}
	}
)

export const verifyOtp = createAsyncThunk<
	VerifyOtpResponse,
	OtpPayload,
	{ rejectValue: ErrorResponse }
>("auth/verifyOtp", async ({ otp, user }: OtpPayload, { rejectWithValue }) => {
	const headerConfig = {
		headers: {
			"Content-Type": "application/json",
		},
		withCredentials: true,
	}
	try {
		const response: AxiosResponse<VerifyOtpResponse> = await axios.post(
			`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiauthenticate/v2/otp`,
			{ otp, user },
			headerConfig
		)
		return response.data
	} catch (error) {
		const axiosError = error as AxiosError<ErrorResponse>
		const backendError = axiosError.response?.data
		return rejectWithValue(backendError ?? { message: "generic" })
	}
})

export const updateProfile = createAsyncThunk<
	AuthResponse,
	IPatchPayload,
	{ rejectValue: ErrorResponse }
>(
	"profile/update",
	async ({ profileData, idUser }: IPatchPayload, { rejectWithValue }) => {
		try {
			const formData = new FormData()
			Object.keys(profileData).forEach((key: string) => {
				formData.append(
					key,
					["currentPassword", "newPassword", "repeatPassword"].includes(key)
						? btoa(profileData[key])
						: profileData[key]
				)
			})

			const headerConfig: AxiosRequestConfig = {
				withCredentials: true,
			}
			const response: AxiosResponse<AuthResponse> = await axios.patch(
				`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiuser/v2/updateProfile/${idUser}`,
				formData,
				headerConfig
			)
			return response.data
		} catch (error) {
			return rejectWithValue(error?.response)
		}
	}
)
